import { useCallback, useEffect, useMemo } from "react";
import {
  annotationTodoStatuses,
  Status,
  todoStatuses,
} from "../../common/status/status";
import { GroupBlockEntityType } from "../";
import { useAppDispatch, useAppSelector } from "../../app";
import {
  changeActiveButton,
  changeActiveEntity,
  changeAnnotating,
  changeScrollToEntity,
} from "../../app/store/appSlice";
import useModifierKeyListener from "../../common/utilities/hooks/useModifierKeyListener";
import { Keys } from "../../common/constants";
import useRemoveActiveEntityAnnotations from "./useRemoveActiveEntityAnnotations";

const useHotKeyListener = (
  hideNonRequiredEntities: boolean,
  disabled: boolean,
  entityTypes?: Array<GroupBlockEntityType> | null,
  multipleGroupBlocks?: boolean,
) => {
  const dispatch = useAppDispatch();
  const { activeEntity, activeButton, isEditing, editAfterExportMode } =
    useAppSelector((state) => state.appReducer);

  const { disabledEntities } = useAppSelector(
    (state) => state.configMapReducer,
  );

  const isAnnotationMode = false;

  const activeDocumentSet = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet,
  );

  const modifierKeyPressed = useModifierKeyListener();

  const removeActiveEntityAnnotations =
    useRemoveActiveEntityAnnotations(multipleGroupBlocks);

  const breakEarly = useMemo((): boolean => {
    if (disabled) {
      return true;
    }

    if (!isAnnotationMode) {
      // In edit-labels mode the document set has already been exported/approved,
      // so its status is Exported/Approved. Those are valid editable states here,
      // otherwise hotkeys (e.g. Escape to deselect the active entity) would be
      // blocked entirely.
      const allowedStatuses = editAfterExportMode
        ? [
            ...todoStatuses,
            Status.Error,
            Status.ExportFailed,
            Status.Exported,
            Status.Approved,
          ]
        : [...todoStatuses, Status.Error, Status.ExportFailed];

      const status: Status | undefined = activeDocumentSet?.status;
      if (!status || !allowedStatuses.includes(status)) {
        return true;
      }
    } else {
      const status: Status | undefined = activeDocumentSet?.status;
      if (!status || !annotationTodoStatuses.includes(status)) {
        return true;
      }
    }

    if (isEditing) {
      return true;
    }

    if (!entityTypes?.length) {
      return true;
    }

    return modifierKeyPressed;
  }, [
    disabled,
    isAnnotationMode,
    activeDocumentSet?.status,
    entityTypes?.length,
    isEditing,
    editAfterExportMode,
    modifierKeyPressed,
  ]);

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (breakEarly) {
        return;
      }

      if (activeButton && event.key.toUpperCase() === Keys.ENTER) {
        document.getElementById(activeButton.id)?.click();
        return;
      }

      if (
        event.key.toUpperCase() === Keys.ESCAPE ||
        event.key.toUpperCase() === Keys.ENTER
      ) {
        dispatch(changeActiveEntity(undefined));
        dispatch(changeActiveButton(undefined));
        dispatch(changeAnnotating(false));
        return;
      }

      if (event.key.toUpperCase() === Keys.BACKSPACE) {
        const target = event.target as HTMLElement | null;
        if (
          target?.isContentEditable ||
          target?.tagName === "INPUT" ||
          target?.tagName === "TEXTAREA"
        ) {
          return;
        }

        // Every mounted group block listens on `document`; matching on the
        // group-block-entity id keeps only the owning one deleting.
        const ownsActiveEntity = entityTypes!.some(
          (entity) =>
            entity.id === activeEntity?.id &&
            !disabledEntities.includes(entity.entityType.id) &&
            !entity.entityType.isComputed,
        );

        if (!ownsActiveEntity) {
          return;
        }

        event.preventDefault();
        removeActiveEntityAnnotations();
        return;
      }

      entityTypes!.forEach((entity) => {
        if (disabledEntities.includes(entity.entityType.id)) {
          return;
        }

        if (entity.entityType.isComputed) {
          return;
        }

        if (hideNonRequiredEntities && !entity.required) {
          return;
        }

        const hotkey = entity?.hotkey?.toUpperCase();
        if (hotkey && hotkey === event.key.toUpperCase()) {
          if (activeEntity && activeEntity.hotkey === hotkey) {
            dispatch(changeActiveEntity(undefined));
            dispatch(changeAnnotating(false));
          } else {
            let index = 1;
            if (multipleGroupBlocks && activeEntity?.index) {
              index = activeEntity.index;
            }

            dispatch(
              changeActiveEntity({
                ...entity,
                index,
              }),
            );
            dispatch(changeAnnotating(true));
            dispatch(changeScrollToEntity({ ...entity, index }));
          }
        }
      });
    },
    [
      breakEarly,
      activeButton,
      entityTypes,
      dispatch,
      disabledEntities,
      hideNonRequiredEntities,
      activeEntity,
      multipleGroupBlocks,
      removeActiveEntityAnnotations,
    ],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyEvent);

    return () => {
      document.removeEventListener("keydown", handleKeyEvent);
    };
  }, [handleKeyEvent]);
};

export default useHotKeyListener;
